import { computed, effect, inject, Injectable, signal } from "@angular/core";
import { ProposalStatus } from "@app/core/domain/proposal-status";
import { Constants } from "@core/constants";
import { Course } from "@core/domain/course";
import { ExamComponent, Minor } from "@core/domain/exam-component";
import { CourseHelper } from "@core/domain/helpers/course-helper";
import { PlanHelper } from "@core/domain/helpers/plan-helper";
import { PlanDetails } from "@core/domain/plan-details";
import { PlanLater, Specialisation } from "@core/domain/plan-later";
import { PlanLaterCourseGroupBuilder } from "@feature/plan-later/data/plan-later-course-group.builder";
import { PlanStateService } from "@shared/services/plan-state.service";
import { CourseData, CourseDataGroup, SelectedPeriod } from "@shared/types/course-data";
import { PlanLaterType } from "@shared/types/plan-later-types";

@Injectable({
  providedIn: "root",
})
export class PlanLaterStateService {
  protected readonly Constants = Constants;

  // public for unit tests
  public courseGroupBuilder: PlanLaterCourseGroupBuilder;

  private readonly courseHelper = inject(CourseHelper);
  private readonly planHelper = inject(PlanHelper);
  private readonly planStateService = inject(PlanStateService);

  minorCourseGroups = signal<CourseDataGroup[]>([]);
  specialisationCourseGroups = signal<CourseDataGroup[]>([]);

  mandatoryGroups = computed<CourseDataGroup[]>(() => {
    const selectedPlan = this.planStateService.currentSelectedPlan();
    const planLater = this.planStateService.planLater();

    return planLater?.mandatory
      ? planLater.mandatory
          .filter((examComponent) => !examComponent.conditionsMetMandatoryOrMandatoryChoice)
          .map((examComponent) =>
            this.courseGroupBuilder.buildExamComponentCourseGroup(examComponent, PlanLaterType.MANDATORY, selectedPlan),
          )
      : [];
  });

  mandatoryChoiceGroups = computed<CourseDataGroup[]>(() => {
    const selectedPlan = this.planStateService.currentSelectedPlan();
    const planLater = this.planStateService.planLater();

    return planLater?.mandatoryChoice
      ? planLater.mandatoryChoice
          .filter((examComponent) => !examComponent.conditionsMetMandatoryOrMandatoryChoice)
          .map((examComponent) =>
            this.courseGroupBuilder.buildExamComponentCourseGroup(
              examComponent,
              PlanLaterType.MANDATORY_CHOICE,
              selectedPlan,
            ),
          )
      : [];
  });

  readonly specialisationGroups = computed<CourseDataGroup[]>(() => {
    const selectedPlan = this.planStateService.currentSelectedPlan();
    const planLater = this.planStateService.planLater();
    const specialisationCourseGroup = this.specialisationCourseGroups();

    const specialisationGroup = planLater?.specialisation
      ? planLater.specialisation.specialisations
          .filter((specialisation) => specialisation.examComponents?.length)
          .filter((specialisation) =>
            specialisation.examComponents.some((examComponent) => examComponent.courses?.length),
          )
          .map((specialisation) => {
            const examComponents = specialisation.examComponents.filter(
              (examComponent) => examComponent.courses?.length,
            );
            return this.courseGroupBuilder.buildSpecialisationCourseGroup(
              specialisation,
              examComponents,
              selectedPlan,
              this,
            );
          })
      : [];

    if (!specialisationGroup) {
      this.specialisationCourseGroups.set(specialisationCourseGroup);
    }

    // Check if the added specialisation is already in the planLater specialisationGroup
    specialisationCourseGroup.forEach((specialisationCourse) => {
      if (!specialisationGroup.some((specialisation) => specialisation.title === specialisationCourse.title)) {
        specialisationGroup.push(specialisationCourse);
      }
    });

    return specialisationGroup;
  });

  showElectives(): boolean {
    return (
      this.planStateService.currentSelectedPlan() === undefined ||
      this.planStateService.currentSelectedPlan()?.proposalStatus !== ProposalStatus.APPROVED
    );
  }

  readonly electivesMinorGroups = computed<CourseDataGroup[]>(() => {
    const selectedPlan = this.planStateService.currentSelectedPlan();
    const planLater = this.planStateService.planLater();
    const minorCourses = this.minorCourseGroups();

    if (!planLater?.electives) {
      return [];
    }

    const profilingMinorGroup = planLater?.electives.minors
      ? planLater.electives.minors
          .filter((minor) => minor.examComponents?.length)
          .map((minor) => {
            const examComponents = minor.examComponents.filter((examComponent) => examComponent.courses?.length);
            return this.courseGroupBuilder.buildMinorCourseGroup(minor, examComponents, selectedPlan, this);
          })
      : [];

    if (!minorCourses) {
      this.minorCourseGroups.set(profilingMinorGroup);
    }

    // Check if the added minorCourse is already in the planLater electivesMinorGroup
    minorCourses.forEach((minorCourse) => {
      if (!profilingMinorGroup.some((profilingMinor) => profilingMinor.title === minorCourse.title)) {
        profilingMinorGroup.push(minorCourse);
      }
    });

    return profilingMinorGroup;
  });

  readonly electivesCoursesGroup = computed<CourseDataGroup>(() => {
    const selectedPlan = this.planStateService.currentSelectedPlan();
    const planLater = this.planStateService.planLater();

    return this.courseGroupBuilder.buildCoursesGroup(
      planLater?.electives ? planLater.electives.examComponentName : "",
      undefined,
      undefined,
      planLater?.electives?.courses ?? [],
      PlanLaterType.ELECTIVES_COURSES,
      selectedPlan,
    );
  });

  readonly otherCoursesGroup = computed<CourseDataGroup>(() => {
    const selectedPlan = this.planStateService.currentSelectedPlan();
    const planLater = this.planStateService.planLater();

    return this.courseGroupBuilder.buildCoursesGroup(
      "planLater.otherCourses",
      undefined,
      undefined,
      planLater?.otherCourses ?? [],
      PlanLaterType.OTHER_COURSES,
      selectedPlan,
    );
  });

  readonly replacementsCoursesGroup = computed<CourseDataGroup>(() => {
    const selectedPlan = this.planStateService.currentSelectedPlan();
    const planLater = this.planStateService.planLater();

    return this.courseGroupBuilder.buildCoursesGroup(
      "planLater.replacements",
      undefined,
      undefined,
      planLater?.replacements ?? [],
      PlanLaterType.REPLACEMENT_COURSES,
      selectedPlan,
    );
  });

  constructor() {
    this.courseGroupBuilder = new PlanLaterCourseGroupBuilder();
    effect(() => {
      const plan = this.planStateService.currentSelectedPlan();
      if (plan) {
        this.updatePlanLaterWithSelectedPlanCourseData(plan);
      }
    });
  }

  addOtherCourse(otherCourse: Course): void {
    // Setting the updated planLater will trigger the effect to update the planLater
    this.planStateService.planLater.update((currentPlanLater) => {
      if (!currentPlanLater?.otherCourses.includes(otherCourse)) {
        currentPlanLater?.otherCourses.push(otherCourse);
        return { ...currentPlanLater } as PlanLater;
      }

      return currentPlanLater;
    });
  }

  updatePlanLaterWithUpdatedCourseData(updatedCourse: Course, selectedPeriod?: SelectedPeriod): void {
    this.planStateService.planLater.update((currentPlanLater) => {
      const planLater = { ...currentPlanLater } as PlanLater;

      planLater?.mandatory?.forEach((examComponent) =>
        this.updateCourses(examComponent, updatedCourse, selectedPeriod),
      );
      planLater?.mandatoryChoice?.forEach((examComponent) =>
        this.updateCourses(examComponent, updatedCourse, selectedPeriod),
      );
      planLater?.specialisation?.specialisations?.forEach((specialisation) =>
        specialisation.examComponents?.forEach((examComponent) =>
          this.updateCourses(examComponent, updatedCourse, selectedPeriod),
        ),
      );
      planLater?.otherCourses
        ?.filter((course) => this.courseHelper.isSameCourse(course, updatedCourse))
        .forEach((course) => this.updateCourse(course, updatedCourse, selectedPeriod));
      planLater?.electives?.courses
        ?.filter((course) => this.courseHelper.isSameCourse(course, updatedCourse))
        .forEach((course) => this.updateCourse(course, updatedCourse, selectedPeriod));
      planLater?.electives?.minors?.forEach((minor) =>
        minor.examComponents?.forEach((examComponent) =>
          this.updateCourses(examComponent, updatedCourse, selectedPeriod),
        ),
      );
      planLater?.replacements
        ?.filter((course) => this.courseHelper.isSameCourse(course, updatedCourse))
        .forEach((course) => this.updateCourse(course, updatedCourse, selectedPeriod));

      return planLater;
    });
  }

  removeCourse(courseData: CourseData): void {
    courseData.isAddedToPlanLater = false;
    courseData.isPartOfPlanLater = false;
    this.planStateService.planLater.update((currentPlanLater) => {
      switch (courseData.planLaterType) {
        case PlanLaterType.MANDATORY:
          currentPlanLater?.mandatory.forEach((examComponent) => {
            examComponent.courses = this.removeCourseFromCourses(examComponent.courses, courseData);
          });
          break;

        case PlanLaterType.MANDATORY_CHOICE:
          currentPlanLater?.mandatoryChoice.forEach((examComponent) => {
            examComponent.courses = this.removeCourseFromCourses(examComponent.courses, courseData);
          });
          break;

        case PlanLaterType.SPECIALISATION:
          currentPlanLater?.specialisation?.specialisations.forEach((specialisation) =>
            specialisation.examComponents.forEach((examComponent) => {
              examComponent.courses = this.removeCourseFromCourses(examComponent.courses, courseData);
            }),
          );
          break;

        case PlanLaterType.OTHER_COURSES:
          currentPlanLater!.otherCourses = this.removeCourseFromCourses(currentPlanLater!.otherCourses, courseData);
          break;

        case PlanLaterType.ELECTIVES_COURSES:
          currentPlanLater!.electives.courses = this.removeCourseFromCourses(
            currentPlanLater!.electives.courses,
            courseData,
          );
          break;

        case PlanLaterType.ELECTIVES_MINOR:
          currentPlanLater?.electives.minors.forEach((minor) =>
            minor.examComponents.forEach((examComponent) => {
              examComponent.courses = this.removeCourseFromCourses(examComponent.courses, courseData);
            }),
          );
          break;

        case PlanLaterType.REPLACEMENT_COURSES:
          currentPlanLater!.replacements = this.removeCourseFromCourses(currentPlanLater!.replacements, courseData);
          break;
      }

      return { ...currentPlanLater } as PlanLater;
    });

    this.planStateService.markPlanAsModified(true);
  }

  removeSpecialisation(specialisation: Specialisation): void {
    this.planStateService.planLater.update((currentPlanLater) => {
      if (currentPlanLater?.specialisation) {
        currentPlanLater.specialisation.specialisations = currentPlanLater.specialisation.specialisations.filter(
          (spec) => !this.planHelper.specialisationEquals(spec, specialisation),
        );
        return { ...currentPlanLater };
      }

      return currentPlanLater;
    });

    this.planStateService.markPlanAsModified(true);
  }

  private minorEquals(minor1: Minor, minor2: Minor): boolean {
    return minor1.minor === minor2.minor && minor1.name === minor2.name;
  }

  removeMinor(minor: Minor): void {
    this.planStateService.planLater.update((currentPlanLater) => {
      if (currentPlanLater?.electives.minors) {
        currentPlanLater.electives.minors = currentPlanLater.electives.minors.filter(
          (mnr) => !this.minorEquals(mnr, minor),
        );

        return { ...currentPlanLater };
      }

      return currentPlanLater;
    });

    this.planStateService.markPlanAsModified(true);
  }

  addMinorCourseToPlanLater(minorToAdd: Minor): void {
    // Update planLater with this minorHit
    this.planStateService.planLater.update((currentPlanLater) => {
      const planLaterHasMinor = currentPlanLater?.electives.minors.some(
        (mr) => mr.studyProgram === minorToAdd.studyProgram && mr.name === minorToAdd.name,
      );
      if (!planLaterHasMinor) {
        currentPlanLater?.electives.minors.push(minorToAdd);

        return { ...currentPlanLater } as PlanLater;
      }

      return currentPlanLater;
    });

    this.planStateService.markPlanAsModified(true);
  }

  addSpecialisationCourseToPlanLater(specialisationToAdd?: Specialisation): void {
    if (specialisationToAdd === undefined) {
      console.warn("Specialisation not found in specialisationCourses! Should not be possible!");
      return;
    }

    // Update planLater with this specialisationHit
    this.planStateService.planLater.update((currentPlanLater) => {
      const planLaterHasSpecialisation = currentPlanLater?.specialisation?.specialisations.some(
        (spec) => spec.code === specialisationToAdd.code,
      );

      if (!planLaterHasSpecialisation) {
        currentPlanLater?.specialisation?.specialisations.push(specialisationToAdd);
        return { ...currentPlanLater } as PlanLater;
      }

      return currentPlanLater;
    });

    this.planStateService.markPlanAsModified(true);
  }

  private updateInfoCourses(courses: Course[], selectedPlan: PlanDetails): void {
    courses.forEach((course) => this.courseHelper.updateCoursePeriodInfo(course, selectedPlan));
  }

  private updateInfoExamComponents(examComponents: ExamComponent[] | undefined, selectedPlan: PlanDetails): void {
    if (examComponents) {
      examComponents.forEach((examComponent) => this.updateInfoCourses(examComponent.courses, selectedPlan));
    }
  }

  private updatePlanLaterWithSelectedPlanCourseData(selectedPlan: PlanDetails): void {
    this.planStateService.planLater.update((currentPlanLater) => {
      this.updateInfoExamComponents(currentPlanLater?.mandatory, selectedPlan);
      this.updateInfoExamComponents(currentPlanLater?.mandatoryChoice, selectedPlan);
      this.updateInfoExamComponents(
        currentPlanLater?.specialisation?.specialisations.flatMap((specialisation) => specialisation.examComponents),
        selectedPlan,
      );
      this.updateInfoCourses(currentPlanLater?.otherCourses ?? [], selectedPlan);
      this.updateInfoCourses(currentPlanLater?.electives?.courses ?? [], selectedPlan);
      this.updateInfoExamComponents(
        currentPlanLater?.electives?.minors.flatMap((minor) => minor.examComponents),
        selectedPlan,
      );
      this.updateInfoCourses(currentPlanLater?.replacements ?? [], selectedPlan);

      return { ...currentPlanLater } as PlanLater;
    });
  }

  findMinorInPlanLater(minorCode: string, minorStudyProgram: string): Minor | undefined {
    return this.planStateService
      .planLater()
      ?.electives.minors.find((minor) => minor.minor === minorCode && minor.studyProgram === minorStudyProgram);
  }

  findSpecialisationInPlanLater(specialisationCode: string, studyProgramCode: string): Specialisation | undefined {
    return this.planStateService.planLater()?.specialisation?.specialisations.find((spec) => {
      return spec.code === specialisationCode && spec.studyProgramCode === studyProgramCode;
    });
  }

  /**
   * Return first specialisation with same code but different specialisation study program from PlanLater, undefined if not found.
   */
  findSpecialisationWithSameNameDifferentProgramInPlanLater(
    specialisation: string,
    studyProgram: string,
  ): Specialisation | undefined {
    return this.planStateService.planLater()?.specialisation?.specialisations.find((spec) => {
      return spec.code === specialisation && spec.studyProgramCode !== studyProgram;
    });
  }

  getPlanLater(): PlanLater | undefined {
    return this.planStateService.planLater();
  }

  findCourseInPlanLater(courseCode?: string): Course | undefined {
    if (!courseCode) {
      // this is never needed for a custom course, because that is always (also) available in the plan grid
      return undefined;
    }
    const planLater = this.getPlanLater();
    if (!planLater) {
      return undefined;
    }

    // For the mandatory and mandatory choice, search in the groups.
    // The course may be present in the planLater object, but not displayed,
    // because it was filtered out in the groups.
    const mandatoryCourse = this.mandatoryGroups()
      ?.flatMap((courseDataGroup) => courseDataGroup.courseLists)
      ?.flatMap((courseList) => courseList.courseDataList)
      .find((courseData) => courseData.code === courseCode);
    const mandatoryChoiceCourse = this.mandatoryChoiceGroups()
      ?.flatMap((courseDataGroup) => courseDataGroup.courseLists)
      ?.flatMap((courseList) => courseList.courseDataList)
      .find((courseData: CourseData) => courseData.code === courseCode);

    // For the other plan later sections, search in the planLater object.
    const specialisationCourse = planLater.specialisation?.specialisations
      .flatMap((specialisation) => specialisation.examComponents)
      .flatMap((examComponent) => examComponent.courses)
      .find((course) => course.code === courseCode);
    const existingOtherCourse = planLater.otherCourses?.find((course) => course.code === courseCode);
    let electivesCourse;
    let minorCourse;
    if (this.showElectives()) {
      electivesCourse = planLater.electives?.courses
        ? planLater.electives?.courses.find((course) => course.code === courseCode)
        : undefined;
      minorCourse = planLater.electives?.minors
        ? planLater.electives.minors
            .flatMap((minor) => minor.examComponents)
            .flatMap((examComponent) => examComponent.courses)
            .find((course) => course.code === courseCode)
        : undefined;
    }
    const replacementCourse = planLater.replacements?.find((course) => course.code === courseCode);
    return (
      mandatoryCourse ??
      mandatoryChoiceCourse ??
      specialisationCourse ??
      existingOtherCourse ??
      electivesCourse ??
      minorCourse ??
      replacementCourse
    );
  }

  enrichCourseDataWithPlanLaterData(courseData: CourseData): CourseData {
    const courseInPlanLater = this.findCourseInPlanLater(courseData.code);
    if (courseInPlanLater) {
      courseData.isPartOfPlanLater = true;
      courseData.isAddedToPlanLater = this.isCourseAddedToPlanLater(courseData);
      courseData.statusObtained = courseInPlanLater.statusObtained;
      courseData.id = courseInPlanLater.id;
    } else {
      courseData.isPartOfPlanLater = false;
      courseData.isAddedToPlanLater = false;
    }
    return courseData;
  }

  /**
   * Check if the course does not exist in the plan grid and does exist in Plan Later in the mandatory exam components,
   * the mandatory choice exam components, or the other courses.
   * It does not check the specialisations or the electives (minors and courses). (why not?)
   */
  isCourseAddedToPlanLater(courseData: CourseData): boolean {
    if (courseData.isCustomCourse) {
      // if this is called for a custom course, we know it is not in plan later
      return false;
    }
    return (
      !courseData.isPlannedCourse &&
      (this.isCourseInPlanLaterOtherCourses(courseData.code) || this.isCourseInPlanLaterMandatory(courseData.code!))
    );
  }

  public isCourseInPlanLaterOtherCourses(courseCode?: string): boolean {
    if (!courseCode) {
      // if this is called for a custom course, we know that it is not in plan later
      return false;
    }
    const planLater = this.planStateService.planLater();
    return !!planLater?.otherCourses?.some((course) => course.code === courseCode);
  }

  private isCourseInPlanLaterMandatory(courseCode: string): boolean {
    const planLater = this.planStateService.planLater();

    return (
      (planLater?.mandatory
        ?.flatMap((examComponent) => examComponent.courses)
        .some((course) => course.code === courseCode) ??
        false) ||
      (planLater?.mandatoryChoice
        ?.flatMap((examComponent) => examComponent.courses)
        .some((course) => course.code === courseCode) ??
        false)
    );
  }

  private updateCourses(examComponent: ExamComponent, updatedCourse: Course, selectedPeriod?: SelectedPeriod): void {
    examComponent.courses
      .filter((course) => this.courseHelper.isSameCourse(course, updatedCourse))
      .forEach((course) => {
        return this.updateCourse(course, updatedCourse, selectedPeriod);
      });
  }

  private updateCourse(course: Course, updatedCourse: Course, selectedPeriod?: SelectedPeriod): void {
    course.isInPlanLater = updatedCourse.isInPlanLater;
    if (selectedPeriod) {
      course.yearNr = selectedPeriod.year.yearNr;
      course.periodDescription = selectedPeriod.period.periodDescription;
    } else {
      // Course is removed from plan
      course.yearNr = 0;
      course.periodNr = 0;
      course.periodDescription = "";
    }
  }

  private removeCourseFromCourses(courses: Course[], courseData: CourseData): Course[] {
    return courses.filter((course) => !this.courseHelper.isSameCourse(course, courseData));
  }
}
